/**
 * HTTP-backed implementation of `RenewalSummaryPort` (TG-4,
 * ADR-20261015-9wt7), over the published portal API route this feature's
 * own TG-2 built: `GET /api/portal/summary`. The first real upstream
 * caller of this port. Its in-memory stub factory and sample fixture stay
 * under `./stubs/` (amended AC-1) — see `renewal-summary-port.ts`'s own
 * doc comment — and App.tsx's composition root no longer constructs them.
 *
 * ADR-20261015-5ene: the renewal is resolved entirely from the session's
 * own claim at the portal API — this route takes no request identifier.
 * `readSummary`'s `renewalId` parameter is therefore ignored here (kept
 * only so this implementation satisfies the unchanged `RenewalSummaryPort`
 * interface every caller, test double and the stub alike
 * already code against). Passing a different renewalId value changes
 * nothing about which request is made or what is returned.
 *
 * ADR-20260918-gvy0: a read has exactly two outcomes, Succeeded or
 * Failed — never Indeterminate. Every non-200 response and every
 * rejected `authenticatedFetch` call (a dropped network connection, or a
 * malformed/unreadable JSON body) maps to `Failed`. A 401 that
 * `authenticatedFetch`'s own one-shot refresh cannot clear is not treated
 * specially here: `authenticatedFetch` has already fired
 * `notifySessionEnded()` (`portal-auth/token.ts`'s session-ended path,
 * DEBT-20261009-dmv3) before this function ever sees the 401 response it
 * still returns, and this function maps that non-200 status to `Failed`
 * exactly like any other — the caller's retry control stays meaningful
 * (AC-9) while the session-ended navigation (App.tsx's own registered
 * handler) proceeds independently.
 */
import { authenticatedFetch } from "../portal-auth/authenticated-fetch";
import type { ReadOutcome } from "./outcome";
import type { RenewalSummaryPort, RenewalSummaryReadResult } from "./renewal-summary-port";

const SUMMARY_ROUTE = "/api/portal/summary";

function failedOutcome(httpStatus: number, message: string): ReadOutcome<RenewalSummaryReadResult> {
  return { status: "Failed", detail: { httpStatus, message } };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * DEBT-20261019-9z2k: the minimal structural check the render path depends
 * on, returning the name of the first invalid field or `undefined`. A 200
 * whose `summary` lacks `policiesInScope`, or carries a policy with no
 * `lines`/`lineCodes` array, would otherwise reach `formatPolicyLine` and
 * `toRenewalData` during render and throw into the error boundary instead of
 * yielding the retry-capable `Failed` state (AC-4). Deliberately narrow: the
 * published contract stays the single source of truth for every other field
 * (vendored and diffed by `state/contracts/conformance.test.ts`), so this
 * names only the keys the landing render reads as collections.
 */
function findInvalidSummaryField(summary: unknown): string | undefined {
  if (!isRecord(summary)) {
    return "summary";
  }
  const { policiesInScope } = summary;
  if (!Array.isArray(policiesInScope)) {
    return "summary.policiesInScope";
  }
  for (const [index, policy] of policiesInScope.entries()) {
    if (!isRecord(policy)) {
      return `summary.policiesInScope[${index}]`;
    }
    if (!Array.isArray(policy.lines)) {
      return `summary.policiesInScope[${index}].lines`;
    }
    if (!Array.isArray(policy.lineCodes)) {
      return `summary.policiesInScope[${index}].lineCodes`;
    }
  }
  return undefined;
}

/**
 * Reads the decoded JSON body's `summary` field as a
 * `RenewalSummaryReadResult`, after the minimal shape check above — the
 * published contract (`src/api/contracts/renewal-summary.contract.json`) is
 * otherwise this field's own source of truth, vendored and diffed by
 * `state/contracts/renewal-summary.contract.json` and its
 * `conformance.test.ts`, not re-validated a second time at runtime here.
 * A response that fails to parse as JSON at all, carries no readable
 * `summary` field, or carries one that fails the shape check, is handled by
 * the caller's own try/catch, which maps it to the same `Failed` outcome a
 * malformed/absent body already takes; the message names the invalid field.
 */
async function readSummaryField(response: Response): Promise<RenewalSummaryReadResult> {
  const body = (await response.json()) as { summary?: unknown };
  if (body.summary === undefined) {
    throw new Error("GET /api/portal/summary: 200 response carried no readable \"summary\" field.");
  }
  const invalidField = findInvalidSummaryField(body.summary);
  if (invalidField !== undefined) {
    throw new Error(`GET /api/portal/summary: 200 response carried an invalid "${invalidField}" field.`);
  }
  return body.summary as RenewalSummaryReadResult;
}

export function createHttpRenewalSummaryPort(): RenewalSummaryPort & { isSampleData: false } {
  return {
    // ADR-20261016-6xax (AC-30 refresh): a literal `false`, never
    // `boolean` -- this is the one port implementation PortalHeader's
    // sample-data marker (design-system/sample-data-context.ts) must
    // never flag, since it is the first real, non-stub, non-sample
    // upstream caller (ADR-20261015-9wt7's own doc comment).
    isSampleData: false,
    // ADR-20261015-9wt7: no renewalId parameter is declared here at all --
    // TypeScript's structural typing admits an implementation with fewer
    // parameters than RenewalSummaryPort.readSummary declares, which both
    // satisfies the interface and leaves nothing unused for a caller that
    // passes one anyway.
    async readSummary(): Promise<ReadOutcome<RenewalSummaryReadResult>> {
      let response: Response;
      try {
        response = await authenticatedFetch(SUMMARY_ROUTE, { method: "GET" });
      } catch (error) {
        // A thrown fetch (no response at all) is a dropped network
        // connection — the same trigger FIND-35/OBJ-20 already
        // established for RenewalLanding's own resolveNextState and for
        // saveSection's Indeterminate mapping, except a read has no
        // Indeterminate case to map onto (ADR-20260918-gvy0), so this
        // maps straight to Failed, with httpStatus 0 marking "no HTTP
        // response was ever received".
        return failedOutcome(0, error instanceof Error ? error.message : String(error));
      }

      if (response.status !== 200) {
        return failedOutcome(response.status, `GET ${SUMMARY_ROUTE} returned ${response.status}.`);
      }

      try {
        const summary = await readSummaryField(response);
        return { status: "Succeeded", value: summary };
      } catch (error) {
        return failedOutcome(response.status, error instanceof Error ? error.message : String(error));
      }
    },
  };
}
